import type {
  IframeConnectRequest,
  IframeHandshakeRequest,
  IframeHandshakeResponse,
  IframeHasStorageAccessResponse,
  IframeOpenResponse,
  IframeTunnelRequest,
} from '@/common/interop/iframe';
import type { UpMessage } from '@/common/interop/up-messages';

import { getIframeUrl, postMessageToIframe } from '../common/iframe';
import { postMessageToPort, waitForPortMessage } from '../common/port';
import { TunnelResponse, TunnelResponseTopic } from '@/common/interop/tunnel';

async function mustEmbedIframe(iframeUrl: string) {
  const res = await fetch(iframeUrl, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
    },
  });
  if (res.status !== 200) {
    const json = await res.json();
    throw new Error(`failed to embed iframe: ${json?.reason}`);
  }
}

export async function registerIframePort(
  iframe: HTMLIFrameElement,
  interopHost: string,
  appId: string,
) {
  const iframeUrl = getIframeUrl(interopHost, appId, 'agent-iframe.bundle.js');
  await mustEmbedIframe(iframeUrl);

  const channel = new MessageChannel();

  channel.port1.start();
  const handshakePromise = waitForPortMessage<IframeHandshakeResponse>(
    channel.port1,
    ['handshake'],
  );

  const handleLoad = () => {
    postMessageToIframe<IframeHandshakeRequest>(
      iframe,
      interopHost,
      channel.port2,
      {
        topic: 'handshake',
      },
    );
  };
  iframe.addEventListener('load', handleLoad);

  iframe.src = iframeUrl;
  await handshakePromise;

  return channel.port1;
}

export function tunnel(
  port: MessagePort,
  message: Omit<IframeTunnelRequest, 'topic'>,
) {
  postMessageToPort(port, {
    topic: 'tunnel',
    ...message,
  });
  return waitForPortMessage<TunnelResponse>(port, [
    TunnelResponseTopic.IdentityError,
    TunnelResponseTopic.RefreshSession,
    TunnelResponseTopic.SessionError,
    TunnelResponseTopic.StartSession,
  ]);
}

export async function connectWebSocket(
  port: MessagePort,
  message: Omit<IframeConnectRequest, 'topic'>,
) {
  postMessageToPort(port, {
    topic: 'connect',
    ...message,
  });
  await waitForPortMessage<IframeOpenResponse>(port, ['open']);
}

export function closeWebSocket(port: MessagePort) {
  postMessageToPort(port, { topic: 'close' });
}

export function sendWebSocketMessage(port: MessagePort, message: UpMessage) {
  postMessageToPort(port, {
    topic: 'send',
    message: JSON.stringify(message),
  });
}

export async function hasStorageAccess(port: MessagePort) {
  postMessageToPort(port, { topic: 'has-storage-access' });
  const response = await waitForPortMessage<IframeHasStorageAccessResponse>(
    port,
    ['has-storage-access'],
    -1,
  );
  return response.hasStorageAccess;
}
